"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo } from "react";
import { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { Tooltip } from "react-tooltip";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import { ApiDropMetadata } from "@/generated/models/ApiDropMetadata";
import { TraitsData } from "../memes/submission/types/TraitsData";
import { FIELD_TO_LABEL_MAP, MEME_TRAITS_SORT_ORDER } from "../memes/traits/schema";
import { isNumber } from "lodash";
interface SingleWaveDropTraitsProps {
  readonly drop: ExtendedDrop;
}

// Component to display individual metadata items in cards
const MetadataItem: React.FC<{
  label: string;
  value: string | number | boolean;
}> = t0 => {
  const $ = _c(15);
  const {
    label,
    value
  } = t0;
  const isMobile = useIsMobileDevice();
  const displayValue = typeof value === "boolean" ? value ? "Yes" : "No" : String(value);
  let t1;
  if ($[0] !== label) {
    t1 = <span className="tw-text-iron-400 tw-text-xs tw-mr-1.5 tw-font-normal">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `trait-${label}-${displayValue}`;
  let t3;
  if ($[2] !== displayValue || $[3] !== t2) {
    t3 = <span className="tw-text-iron-50 tw-text-sm tw-font-medium tw-truncate" data-tooltip-id={t2}>{displayValue}</span>;
    $[2] = displayValue;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== displayValue || $[6] !== isMobile || $[7] !== label) {
    t4 = !isMobile && <Tooltip id={`trait-${label}-${displayValue}`} place="top" style={{
      backgroundColor: "#1F2937",
      color: "white",
      padding: "4px 8px"
    }}>{displayValue}</Tooltip>;
    $[5] = displayValue;
    $[6] = isMobile;
    $[7] = label;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t1 || $[13] !== t5) {
    t6 = <div className="tw-px-2 tw-py-1 tw-rounded-md tw-bg-iron-800 tw-flex tw-flex-col tw-gap-y-1.5">{t1}{t5}</div>;
    $[12] = t1;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};

// Extract trait data from drop metadata
const extractTraitsFromMetadata = (metadata: ApiDropMetadata[]): Partial<TraitsData> => {
  // Initialize with empty values for all fields to avoid type errors
  const traits: Partial<TraitsData> = {
    // Text fields
    artist: "",
    seizeArtistProfile: "",
    memeName: "",
    palette: "",
    style: "",
    jewel: "",
    superpower: "",
    dharma: "",
    gear: "",
    clothing: "",
    element: "",
    mystery: "",
    secrets: "",
    weapon: "",
    home: "",
    parent: "",
    sibling: "",
    food: "",
    drink: "",
    bonus: "",
    boost: "",
    title: "",
    description: "",
    // Boolean fields
    punk6529: false,
    gradient: false,
    movement: false,
    dynamic: false,
    interactive: false,
    collab: false,
    om: false,
    threeD: false,
    pepe: false,
    gm: false,
    summer: false,
    tulip: false,
    // Number fields
    pointsPower: 0,
    pointsWisdom: 0,
    pointsLoki: 0,
    pointsSpeed: 0
  };

  // Process each metadata item
  metadata.forEach(item => {
    // Normalize the key by removing spaces, dashes, and converting to lowercase
    const normalizedKey = item.data_key.replace(/[\s-_]+/g, "").replaceAll("points-", "points").replaceAll("type-", "type");
    const value = item.data_value;

    // Map for boolean traits (using normalized keys)
    const booleanTraits = ["punk6529", "gradient", "movement", "dynamic", "interactive", "collab", "om", "threed", "3d", "pepe", "gm", "summer", "tulip"];

    // Map for number traits (using normalized keys)
    const numberTraits = ["pointspower", "pointswisdom", "pointsloki", "pointsspeed"];

    // Comprehensive key mapping for string traits
    const keyMapping: Record<string, keyof TraitsData> = {
      // Basic info
      artist: "artist",
      artistname: "artist",
      memename: "memeName",
      meme: "memeName",
      title: "memeName",
      // Special properties
      palette: "palette",
      colorpalette: "palette",
      style: "style",
      artstyle: "style",
      jewel: "jewel",
      gem: "jewel",
      superpower: "superpower",
      power: "superpower",
      dharma: "dharma",
      gear: "gear",
      equipment: "gear",
      clothing: "clothing",
      outfit: "clothing",
      element: "element",
      bonus: "bonus",
      boost: "boost",
      // Additional details
      mystery: "mystery",
      secrets: "secrets",
      secret: "secrets",
      weapon: "weapon",
      home: "home",
      house: "home",
      parent: "parent",
      sibling: "sibling",
      food: "food",
      drink: "drink",
      beverage: "drink",
      // Special
      seizeartistprofile: "seizeArtistProfile",
      artistprofile: "seizeArtistProfile",
      profilehandle: "seizeArtistProfile"
    };

    // Process based on trait type
    if (booleanTraits.includes(normalizedKey)) {
      // Handle special case for "3d" which maps to "threeD"
      const traitKey = normalizedKey === "3d" ? "threeD" : normalizedKey;

      // Type guard: ensure we're only assigning to known boolean fields
      const booleanKeys: Array<keyof TraitsData> = ["punk6529", "gradient", "movement", "dynamic", "interactive", "collab", "om", "threeD", "pepe", "gm", "summer", "tulip"];
      if (booleanKeys.includes(traitKey as keyof TraitsData)) {
        const boolKey = traitKey as keyof TraitsData;
        const boolValue = value.toLowerCase() === "true" || value === "1" || value.toLowerCase() === "yes";

        // Safe assignment with type checking
        if (typeof traits[boolKey] === "boolean" || traits[boolKey] === undefined) {
          (traits as Record<keyof TraitsData, any>)[boolKey] = boolValue;
        }
      }
    } else if (numberTraits.includes(normalizedKey)) {
      // Map common variations to their canonical property names
      let traitKey: keyof TraitsData | null = null;

      // Type guard: map to known number fields
      if (["pointsPower", "pointsWisdom", "pointsLoki", "pointsSpeed"].includes(normalizedKey as keyof TraitsData)) {
        traitKey = normalizedKey as keyof TraitsData;
      }
      if (traitKey) {
        const numValue = Number(value) || 0;

        // Safe assignment with type checking
        if (typeof traits[traitKey] === "number" || traits[traitKey] === undefined) {
          (traits as Record<keyof TraitsData, any>)[traitKey] = numValue;
        }
      }
    } else if (keyMapping[normalizedKey.toLowerCase()]) {
      const textKey = keyMapping[normalizedKey.toLowerCase()];

      // Safe assignment with type checking
      if (typeof traits[textKey] === "string" || traits[textKey] === undefined) {
        (traits as Record<keyof TraitsData, any>)[textKey] = value;
      }
    }
    // Special case: handle any key that starts with "points"
    else if (normalizedKey.startsWith("points")) {
      const pointType = normalizedKey.replace("points", "");
      const capitalizedPointType = pointType.charAt(0).toUpperCase() + pointType.slice(1);
      if (["Power", "Wisdom", "Loki", "Speed"].includes(capitalizedPointType)) {
        const camelCaseKey = `points${capitalizedPointType}` as keyof TraitsData;
        const pointValue = Number(value) || 0;

        // Safe assignment with type checking
        if (typeof traits[camelCaseKey] === "number" || traits[camelCaseKey] === undefined) {
          (traits as Record<keyof TraitsData, any>)[camelCaseKey] = pointValue;
        }
      }
    }
  });
  return traits;
};
export const SingleWaveDropTraits: React.FC<SingleWaveDropTraitsProps> = t0 => {
  const $ = _c(54);
  const {
    drop
  } = t0;
  const [showAllTraits, setShowAllTraits] = useState(false);
  let t1;
  if ($[0] !== drop.metadata) {
    t1 = extractTraitsFromMetadata(drop.metadata);
    $[0] = drop.metadata;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const extractedTraits = t1;
  const description = drop.parts && drop.parts.length > 0 ? drop.parts[0].content || "" : "";
  const t2 = drop.title || extractedTraits.title || "";
  const t3 = description || extractedTraits.description || "";
  const t4 = extractedTraits.artist ?? "";
  const t5 = extractedTraits.palette ?? "";
  const t6 = extractedTraits.style ?? "";
  const t7 = extractedTraits.jewel ?? "";
  const t8 = extractedTraits.superpower ?? "";
  const t9 = extractedTraits.dharma ?? "";
  const t10 = extractedTraits.gear ?? "";
  const t11 = extractedTraits.clothing ?? "";
  const t12 = extractedTraits.element ?? "";
  const t13 = extractedTraits.mystery ?? "";
  const t14 = extractedTraits.secrets ?? "";
  const t15 = extractedTraits.weapon ?? "";
  const t16 = extractedTraits.home ?? "";
  const t17 = extractedTraits.parent ?? "";
  const t18 = extractedTraits.sibling ?? "";
  const t19 = extractedTraits.food ?? "";
  const t20 = extractedTraits.drink ?? "";
  const t21 = extractedTraits.bonus ?? "";
  const t22 = extractedTraits.boost ?? "";
  const t23 = extractedTraits.punk6529 ?? false;
  const t24 = extractedTraits.gradient ?? false;
  const t25 = extractedTraits.movement ?? false;
  const t26 = extractedTraits.dynamic ?? false;
  const t27 = extractedTraits.interactive ?? false;
  const t28 = extractedTraits.collab ?? false;
  const t29 = extractedTraits.om ?? false;
  const t30 = extractedTraits.threeD ?? false;
  const t31 = extractedTraits.pepe ?? false;
  const t32 = extractedTraits.gm ?? false;
  const t33 = extractedTraits.summer ?? false;
  const t34 = extractedTraits.tulip ?? false;
  const t35 = extractedTraits.memeName ?? "";
  const t36 = extractedTraits.pointsPower ?? 0;
  const t37 = extractedTraits.pointsWisdom ?? 0;
  const t38 = extractedTraits.pointsLoki ?? 0;
  const t39 = extractedTraits.pointsSpeed ?? 0;
  const t40 = extractedTraits.seizeArtistProfile ?? "";
  let t41;
  if ($[2] !== t10 || $[3] !== t11 || $[4] !== t12 || $[5] !== t13 || $[6] !== t14 || $[7] !== t15 || $[8] !== t16 || $[9] !== t17 || $[10] !== t18 || $[11] !== t19 || $[12] !== t2 || $[13] !== t20 || $[14] !== t21 || $[15] !== t22 || $[16] !== t23 || $[17] !== t24 || $[18] !== t25 || $[19] !== t26 || $[20] !== t27 || $[21] !== t28 || $[22] !== t29 || $[23] !== t3 || $[24] !== t30 || $[25] !== t31 || $[26] !== t32 || $[27] !== t33 || $[28] !== t34 || $[29] !== t35 || $[30] !== t36 || $[31] !== t37 || $[32] !== t38 || $[33] !== t39 || $[34] !== t4 || $[35] !== t40 || $[36] !== t5 || $[37] !== t6 || $[38] !== t7 || $[39] !== t8 || $[40] !== t9) {
    t41 = {
      title: t2,
      description: t3,
      artist: t4,
      palette: t5,
      style: t6,
      jewel: t7,
      superpower: t8,
      dharma: t9,
      gear: t10,
      clothing: t11,
      element: t12,
      mystery: t13,
      secrets: t14,
      weapon: t15,
      home: t16,
      parent: t17,
      sibling: t18,
      food: t19,
      drink: t20,
      bonus: t21,
      boost: t22,
      punk6529: t23,
      gradient: t24,
      movement: t25,
      dynamic: t26,
      interactive: t27,
      collab: t28,
      om: t29,
      threeD: t30,
      pepe: t31,
      gm: t32,
      summer: t33,
      tulip: t34,
      memeName: t35,
      pointsPower: t36,
      pointsWisdom: t37,
      pointsLoki: t38,
      pointsSpeed: t39,
      seizeArtistProfile: t40
    };
    $[2] = t10;
    $[3] = t11;
    $[4] = t12;
    $[5] = t13;
    $[6] = t14;
    $[7] = t15;
    $[8] = t16;
    $[9] = t17;
    $[10] = t18;
    $[11] = t19;
    $[12] = t2;
    $[13] = t20;
    $[14] = t21;
    $[15] = t22;
    $[16] = t23;
    $[17] = t24;
    $[18] = t25;
    $[19] = t26;
    $[20] = t27;
    $[21] = t28;
    $[22] = t29;
    $[23] = t3;
    $[24] = t30;
    $[25] = t31;
    $[26] = t32;
    $[27] = t33;
    $[28] = t34;
    $[29] = t35;
    $[30] = t36;
    $[31] = t37;
    $[32] = t38;
    $[33] = t39;
    $[34] = t4;
    $[35] = t40;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
    $[41] = t41;
  } else {
    t41 = $[41];
  }
  const finalTraits = t41;
  const traits = finalTraits;
  let items;
  if ($[42] !== traits) {
    const priorityKeys = ["artist", "seizeArtistProfile", "memeName", "title", "description", "punk6529", "gradient", "movement", "dynamic", "interactive", "collab", "om", "threeD", "pepe", "gm", "summer", "tulip", "style", "palette", "jewel", "superpower", "dharma", "element", "gear", "clothing", "weapon", "home", "parent", "sibling", "food", "drink", "mystery", "secrets", "bonus", "boost", "pointsPower", "pointsWisdom", "pointsLoki", "pointsSpeed"];
    items = [];
    priorityKeys.forEach(key => {
      if (key in traits && traits[key as keyof TraitsData] !== undefined) {
        const value = traits[key as keyof TraitsData];
        if (typeof value === "string" && value || typeof value === "number" || typeof value === "boolean") {
          items.push({
            label: key,
            value
          });
        }
      }
    });
    Object.entries(traits).forEach(t42 => {
      const [key_0, value_0] = t42;
      if (!priorityKeys.includes(key_0)) {
        if (typeof value_0 === "string" && value_0 || typeof value_0 === "number" || typeof value_0 === "boolean") {
          items.push({
            label: key_0,
            value: value_0
          });
        }
      }
    });
    items.sort(_temp);
    items.forEach(_temp3);
    $[42] = traits;
    $[43] = items;
  } else {
    items = $[43];
  }
  const traitItems = items;
  if (traitItems.length === 0) {
    return null;
  }
  let t42;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = e => {
      e.stopPropagation();
      setShowAllTraits(true);
    };
    $[44] = t42;
  } else {
    t42 = $[44];
  }
  const handleShowMore = t42;
  let t43;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = e_0 => {
      e_0.stopPropagation();
      setShowAllTraits(false);
    };
    $[45] = t43;
  } else {
    t43 = $[45];
  }
  const handleShowLess = t43;
  let t44;
  if ($[46] !== traitItems) {
    t44 = traitItems.slice(0, 2).map(_temp4);
    $[46] = traitItems;
    $[47] = t44;
  } else {
    t44 = $[47];
  }
  let t45;
  if ($[48] !== showAllTraits || $[49] !== traitItems) {
    t45 = traitItems.length > 2 && (showAllTraits ? <>{traitItems.slice(2).map(_temp5)}<button onClick={handleShowLess} className="tw-text-xs tw-text-primary-400 desktop-hover:hover:tw-text-primary-300 tw-transition tw-duration-300 tw-ease-out tw-font-semibold tw-bg-transparent tw-border-0 tw-text-left">Show less</button></> : <button onClick={handleShowMore} className="tw-text-xs tw-text-primary-400 desktop-hover:hover:tw-text-primary-300 tw-transition tw-duration-300 tw-ease-out tw-font-semibold tw-bg-transparent tw-border-0 tw-text-left">Show all</button>);
    $[48] = showAllTraits;
    $[49] = traitItems;
    $[50] = t45;
  } else {
    t45 = $[50];
  }
  let t46;
  if ($[51] !== t44 || $[52] !== t45) {
    t46 = <div className="tw-w-full"><div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-3 tw-gap-2">{t44}{t45}</div></div>;
    $[51] = t44;
    $[52] = t45;
    $[53] = t46;
  } else {
    t46 = $[53];
  }
  return t46;
};
function _temp(a, b) {
  const aIndex = MEME_TRAITS_SORT_ORDER.indexOf(a.label);
  const bIndex = MEME_TRAITS_SORT_ORDER.indexOf(b.label);
  return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex);
}
function _temp2(str) {
  return str.toUpperCase();
}
function _temp3(item) {
  const knownKey = FIELD_TO_LABEL_MAP[item.label as keyof typeof FIELD_TO_LABEL_MAP];
  const formattedKey = knownKey ?? item.label.replace(/([A-Z])/g, " $1").replace(/^./, _temp2).trim();
  item.label = formattedKey;
  if (isNumber(item.value)) {
    item.value = item.value.toLocaleString();
  }
}
function _temp4(item_0) {
  return <MetadataItem key={item_0.label} label={FIELD_TO_LABEL_MAP[item_0.label as keyof typeof FIELD_TO_LABEL_MAP] ?? item_0.label} value={item_0.value} />;
}
function _temp5(item_1) {
  return <MetadataItem key={item_1.label} label={FIELD_TO_LABEL_MAP[item_1.label as keyof typeof FIELD_TO_LABEL_MAP] ?? item_1.label} value={item_1.value} />;
}